import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📘',
  number: 'Tomo 1',
  title: 'Arquitectura de la Materia',
  subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
  problem: 'El primer contacto con la química puede ser el más transformador o el más aburrido. Este tomo convierte conceptos abstractos como "átomo" o "tabla periódica" en algo completamente comprensible y relevante desde el primer día de clase.',
  bullets: ['Propiedades de la materia y métodos de separación de mezclas con ejemplos de la minería del cobre en Chile y Perú', 'Evolución histórica de los modelos atómicos explicada sin tecnicismos innecesarios', 'Números cuánticos y configuración electrónica de los elementos estratégicos de LATAM', 'Tabla periódica, propiedades periódicas y los elementos del Triángulo del Litio', 'Ejercicios graduados, evaluaciones listas y conexión permanente con la industria regional'],
  result: 'Tus alumnos entienden el átomo y la tabla periódica como herramientas reales, no como datos que memorizar y olvidar al día siguiente.'
}, {
  icon: '📗',
  number: 'Tomo 2',
  title: 'Enlace Químico y Nomenclatura',
  subtitle: 'El lenguaje de la química: cómo se unen los átomos y cómo se nombra todo',
  problem: 'La nomenclatura es el tema que más tiempo roba y más confunde. Este tomo te da el material listo para explicar los tres sistemas de forma progresiva, con contexto industrial que hace que los alumnos entiendan para qué sirve saber nombrar un compuesto.',
  bullets: ['Enlace iónico, covalente y metálico con ejemplos de materiales industriales de LATAM', 'Teoría RPENV y geometría molecular explicada paso a paso', 'Fuerzas intermoleculares conectadas con propiedades físicas reales', 'Nomenclatura completa de óxidos, ácidos, bases y sales en los tres sistemas', 'Tabla maestra de sales con sus usos en la agroindustria, minería y vida cotidiana de la región'],
  result: 'Tus alumnos dejan de memorizar nombres sin sentido y empiezan a entender qué dice la nomenclatura sobre la composición y las propiedades de cada compuesto.'
}, {
  icon: '📙',
  number: 'Tomo 3',
  title: 'Reacciones Químicas y Estequiometría',
  subtitle: 'La matemática de la química: cuánto se necesita y cuánto se obtiene',
  problem: 'La estequiometría es el punto donde más alumnos se rinden. Este tomo te da el material para enseñarla de forma progresiva, con problemas reales de la industria latinoamericana que hacen que los cálculos tengan sentido.',
  bullets: ['Clasificación de reacciones con ejemplos de procesos industriales reales de la región', 'Balanceo de ecuaciones por tanteo, método algebraico y método redox', 'El mol, la masa molar y las conversiones explicadas con claridad', 'Reactivo limitante y rendimiento con problemas del sector minero y agroindustrial', 'Reacciones en solución acuosa, pH y titulaciones aplicadas al tratamiento de agua en LATAM', 'Reacciones redox y electroquímica: refinación electrolítica del cobre, corrosión, hidrógeno verde'],
  result: 'Tus alumnos hacen cálculos estequiométricos entendiendo qué calculan y para qué, no ejecutando una fórmula que no comprenden.'
}, {
  icon: '📕',
  number: 'Tomo 4',
  title: 'Química Orgánica e Hidrocarburos',
  subtitle: 'El tomo que más dudas genera — y mejor resuelto está en este pack',
  problem: 'La química orgánica es el tema que más alumnos desconecta porque parece abstracto y alejado de su vida. Este tomo lo convierte en el más fascinante del año, conectando cada familia orgánica con la industria, los alimentos y la tecnología que los alumnos usan todos los días.',
  bullets: ['El carbono y sus propiedades únicas desde la catenación y la tetravalencia', 'Alcanos, alquenos y alquinos con ejemplos del petróleo de México, Colombia y Ecuador', 'Hidrocarburos aromáticos y la industria petroquímica de LATAM', 'Alcoholes, éteres, aldehídos, cetonas, ácidos carboxílicos y ésteres con usos reales', 'La saponificación y la industria del jabón; la transesterificación y el biodiesel de soja', 'Polímeros: PET, nylon, PVC, polietileno verde', 'Biomoléculas: carbohidratos, proteínas, lípidos y ácidos nucleicos'],
  result: 'Tus alumnos entienden que la química orgánica está en todo lo que tocan, comen, visten y usan — y eso cambia completamente su relación con la materia.',
  popular: true
}, {
  icon: '📓',
  number: 'Tomo 5',
  title: 'Laboratorio de Guerrilla',
  subtitle: '50 prácticas de alto impacto con materiales de bajo costo',
  problem: 'El problema más común en las escuelas de LATAM: no hay reactivos, no hay equipos, no hay presupuesto. Este tomo resuelve ese problema de raíz con 50 prácticas diseñadas para hacerse con materiales de supermercado, ferretería y farmacia.',
  bullets: ['8 bloques temáticos que cubren desde propiedades de la materia hasta química ambiental', 'Nivel de dificultad graduado: básico, intermedio y avanzado', 'Conexión industrial permanente: cada práctica conecta con un proceso real de la región', 'Destilación artesanal, cristalización, celdas galvánicas con frutas, saponificación, extracción de ADN, fermentación alcohólica y mucho más', 'Informe de laboratorio, rúbrica de evaluación y preguntas de análisis incluidas en cada práctica'],
  result: 'Tenés 50 clases de laboratorio listas para usar aunque tu escuela no tenga un solo frasco de reactivo.'
}, {
  icon: '📔',
  number: 'Tomo 6',
  title: 'Química Ambiental y Sustentabilidad',
  subtitle: 'La química que importa: el planeta, el agua, el suelo y el futuro energético de LATAM',
  problem: 'Los alumnos de hoy necesitan entender la química ambiental porque es la química de su vida cotidiana y de los problemas más urgentes de su región.',
  bullets: ['La atmósfera, el efecto invernadero y el cambio climático con datos reales de LATAM', 'La destrucción de la capa de ozono y su impacto en el Cono Sur', 'Química del agua: arsénico en Chile, Argentina y Bolivia; tratamiento de aguas residuales mineras', 'Química del suelo: ciclos biogeoquímicos, agroquímicos, salinización y acidificación', 'Residuos plásticos, microplásticos y economía circular', 'Energías renovables: paneles solares, hidrógeno verde, baterías de litio del Triángulo del Litio', 'Los 12 Principios de la Química Verde aplicados a la industria de LATAM'],
  result: 'Tus alumnos terminan el año con una visión química del mundo que los convierte en ciudadanos más conscientes sobre los problemas más urgentes de su región.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-petrol" data-id="element-301">
      <div className="max-w-6xl mx-auto" data-id="element-302">
        <div className="text-center mb-16" data-id="element-303">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-white" data-id="element-304">
            📘 Los 6 Tomos del Mega Pack
          </h2>
          <p className="text-xl text-gold-light" data-id="element-305">
            Material completo para el aula
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-306">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-night-card rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-night-border'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-307">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-black font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-308">
                  EL MÁS POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-309">
                <span className="text-4xl" data-id="element-310">{tomo.icon}</span>
                <div data-id="element-311">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-312">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1 text-white" data-id="element-313">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-white font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-314">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-315">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-316">
                  ¿Qué resuelve en el aula?
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-317">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-318">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-319">
                    <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" data-id="element-320" />
                    <span className="text-gray-200 text-sm" data-id="element-321">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-auto" data-id="element-322">
                <span className="text-gold font-bold block mb-1" data-id="element-323">
                  Resultado:
                </span>
                <p className="text-white text-sm" data-id="element-324">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}